import React, { useContext, useEffect, useRef, useState } from "react";

import { LaunchOutlined } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from "@mui/material";
import classNames from "classnames";
import { useSnackbar } from "notistack";
import Avatar from "react-avatar";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import {
  apiRequest,
  formatApiRequestError,
  parseCountryFromPhoneNumber,
  UserContext,
} from "../../helpers";
import { useSupportedLanguages } from "../../languages";
import { disabledInputField } from "../../theme";
import countryCodesToTimezones from "../../timezones";
import { ValueType } from "../../types";
import {
  validatePasswordHelper,
  validateSignupEmailHelper,
  validateSignupPhoneNumberHelper,
  validateUserPasswordHelper,
  validateTextHelper,
} from "../../validationHelpers";
import PasswordFieldWithValidation from "../inputs/PasswordFieldWithValidation";
import ModalFormConfirmation from "../modals/ModalFormConfirmation";
import ModalServerTimeOut from "../modals/ModalServerTimeOut";
import PromptUnsavedChanges from "../modals/PromptUnsavedChanges";
import StickyPageHeader from "../pageheader/StickyPageHeader";
import AddressInfoComponent from "./AddressInfoComponent";
import BasicInfoComponent from "./BasicInfoComponent";

const useStyles = makeStyles()((theme: any) => ({
  avatar: {
    color: theme.palette.primary.main,
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarBox: {
    "& > div": {
      display: "block !important",
      margin: "24px auto 40px",
    },
  },
  cancelPasswordButton: {
    color: "red",
    cursor: "pointer",
    minWidth: "fit-content",
  },
  cardBorder: {
    borderTopColor: theme.palette.grey[500],
    borderTopStyle: "solid",
    borderTopWidth: 1,
  },
  cardPadding: {
    padding: theme.spacing(3),
  },
  container: {
    marginBottom: theme.spacing(3),
    "& .MuiCardHeader-title": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
  },
  disabledInputField: disabledInputField,
  fullWidth: {
    width: "100%",
  },
  helperLink: {
    color: theme.palette.secondary.main,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(12),
    textAlign: "left",
    marginTop: -1,
    lineHeight: "1em",
    minHeight: "1em",
    margin: 0,
  },
  infoIcon: {
    position: "relative",
    bottom: -3,
    left: 3,
    cursor: "pointer",
  },
  infoText: {
    color: theme.palette.grey[500],
    fontSize: theme.typography.pxToRem(16),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
    textAlign: "justify",
    "& p:first-of-type": {
      marginBottom: theme.spacing(2),
    },
  },
  lateSettingMins: {
    alignContent: "center",
    alignSelf: "center",
  },
  passwordLink: {
    color: theme.palette.secondary.main,
  },
  textFieldFullWidth: {
    marginTop: theme.spacing(2),
  },
  title: {
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(0.25),
  },
  upperText: {
    textTransform: "uppercase",
  },
}));

interface SettersType {
  [key: string]: Function;
}

const ProfilePage = () => {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { user } = useContext(UserContext);
  const { languageList } = useSupportedLanguages();
  const countryRef = useRef<HTMLInputElement>(null);
  const languageRef = useRef<HTMLInputElement>(null);
  const timezoneRef = useRef<HTMLInputElement>(null);

  const [cautionModalHeader, setCautionModalHeader] = useState("");
  const [cautionModalContent, setCautionModalContent] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState({
    error: false,
    value: "",
    helperText: "Must be the same as the new password.",
  });
  const [country, setCountry] = useState(user.company.countryCode);
  const [currentPassword, setCurrentPassword] = useState({
    error: false,
    value: "",
    helperText: "",
  });
  const [disableEmailField, setDisableEmailField] = useState(false);
  const [disablePhoneField, setDisablePhoneField] = useState(false);
  const [email, setEmail] = useState<ValueType>({
    error: false,
    value: user.email,
    helperText: "",
  });
  const [firstName, setFirstName] = useState<ValueType>({
    error: false,
    value: user.firstName,
    helperText: "",
  });
  const [isEmailValidating, setIsEmailValidating] = useState(false);
  const [isPasswordValidating, setIsPasswordValidating] = useState(false);
  const [isPhoneNumberValidating, setIsPhoneNumberValidating] = useState(false);
  const [language, setLanguage] = useState(user.preferredLanguage);
  const [lateSetting, setLateSeting] = useState(0);
  const [lastName, setLastName] = useState<ValueType>({
    error: false,
    value: user.lastName,
    helperText: "",
  });
  const [newPassword, setNewPassword] = useState<ValueType>({
    error: false,
    value: "",
  });
  const [phoneCountry, setPhoneCountry] = useState({
    value: "US" as string | undefined,
  });
  const [phoneNumber, setPhoneNumber] = useState<ValueType>({
    error: false,
    value: "",
    helperText: "",
  });
  const [phoneNumberDb, setPhoneNumberDb] = useState("");
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);
  const [showCautionModal, setShowCautionModal] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showServerErrorModal, setShowServerErrorModal] = useState(false);
  const [timezone, setTimezone] = useState(user.timezone);
  const [timezoneList, setTimezoneList] = useState(
    countryCodesToTimezones[
      user.company.countryCode as keyof typeof countryCodesToTimezones
    ]
  );

  const setters: SettersType = {
    confirmNewPassword: setConfirmNewPassword,
    country: setCountry,
    currentPassword: setCurrentPassword,
    email: setEmail,
    firstName: setFirstName,
    language: setLanguage,
    lastName: setLastName,
    newPassword: setNewPassword,
    phoneNumber: setPhoneNumber,
    timezone: setTimezone,
    timezoneList: setTimezoneList,
  };

  const userFullName = `${firstName.value} ${lastName.value}`.trim();

  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line
  }, []);

  const checkFormErrors = () => {
    if (isEmailValidating || isPasswordValidating || isPhoneNumberValidating) {
      return false;
    }

    if (!firstName.value || firstName.error) {
      document.getElementsByName("firstName")[0].focus();
      return false;
    }
    if (lastName.error) {
      document.getElementsByName("lastName")[0].focus();
      return false;
    }
    if (!email.value || email.error) {
      document.getElementsByName("email")[0].focus();
      return false;
    }
    if (phoneNumber.error) {
      document.getElementsByName("phoneNumber")[0].focus();
      return false;
    }
    if (country === "" && countryRef.current) {
      countryRef.current.focus();
      return false;
    }
    if (language === "" && languageRef.current) {
      languageRef.current.focus();
      return false;
    }
    if (timezone === "" && timezoneRef.current) {
      timezoneRef.current.focus();
      return false;
    }
    if (currentPassword.error) {
      document.getElementsByName("currentPassword")[0].focus();
      return false;
    }
    if (newPassword.error) {
      document.getElementsByName("newPassword")[0].focus();
      return false;
    }
    if (
      confirmNewPassword.error ||
      newPassword.value !== confirmNewPassword.value
    ) {
      document.getElementsByName("confirmNewPassword")[0].focus();
      return false;
    }
    return true;
  };

  const fetchUserDetails = () => {
    apiRequest({
      endpoint: `v2/companies/${user.company.id}/users/me`,
    })
      .then((response: any) => {
        setFirstName({ ...firstName, value: response.firstName });
        setLastName({ ...lastName, value: response.lastName });
        setEmail({ ...email, value: response.email });
        setPhoneNumber({ ...phoneNumber, value: response.phoneNumber });
        setPhoneCountry({
          value: response.phoneNumber
            ? parseCountryFromPhoneNumber(response.phoneNumber)
            : "US",
        });
        setCountry(response.country);
        setLanguage(response.preferredLanguage);
        setTimezone(response.timezone);
        setTimezoneList(
          response.country !== ""
            ? countryCodesToTimezones[
                response.country as keyof typeof countryCodesToTimezones
              ]
            : []
        );

        // set lateSetting to avoid schema changes
        setLateSeting(response.lateSetting);
        // set phone number on user context, its missing
        setPhoneNumberDb(response.phoneNumber);
      })
      .catch((error) => {
        console.warn(
          "Could not fetch user details",
          formatApiRequestError(error)
        );
        toggleServerErrorModal();
      });
  };

  const handleChangePhoneCountry = (newCountry: string) => {
    setShouldBlockNavigation(true);
    const phoneNumberValue = phoneNumber.value;
    if (phoneNumberValue.length > 0) {
      validatePhoneNumber(phoneNumberValue, newCountry);
    } else {
      setPhoneCountry({ value: newCountry });
    }
  };

  const handleFieldChange = (
    stateGetter: string,
    value: ValueType | string,
    shouldBlock = true as boolean
  ) => {
    setShouldBlockNavigation(shouldBlock);
    const setter = setters[stateGetter];
    if (setter) {
      setter((prevState: ValueType) => ({ ...prevState, value }));
    }
  };

  const handleFieldValueChange = (
    fieldName: string,
    value: ValueType,
    shouldBlock = true as boolean
  ) => {
    if (fieldName === "email") {
      setDisablePhoneField(value.value !== user.email);
    } else if (fieldName === "phoneNumber") {
      setDisableEmailField(value.value !== user.phoneNumber);
    }
    setShouldBlockNavigation(shouldBlock);
    const setter = setters[fieldName];
    setter(value);
  };

  const handleSaveProfile = () => {
    // disable caution modal if shown
    setShowCautionModal(false);

    setSaveButtonDisabled(true);
    const updateUser = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      phoneNumber: phoneNumber.value,
      country: country,
      timezone: timezone,
      preferredLanguage: language,
      newPassword: "" as string | undefined,
      lateSetting: lateSetting,
    };

    let passwordUpdated = false;
    if (
      newPassword.value !== "" &&
      newPassword.value !== null &&
      newPassword.value !== undefined
    ) {
      updateUser.newPassword = newPassword.value;
      passwordUpdated = true;
    } else {
      delete updateUser.newPassword;
    }

    apiRequest({
      method: "PATCH",
      endpoint: `v2/companies/${user.company.id}/users/${user.id}`,
      body: updateUser,
    })
      .then(() => {
        setSaveButtonDisabled(false);
        setShouldBlockNavigation(false);
        enqueueSnackbar(`Profile has been updated successfully`, {
          variant: "success",
        });
        if (passwordUpdated) {
          enqueueSnackbar(`Password has been updated`, {
            variant: "info",
          });
        }
        navigate(-1);
      })
      .catch((error) => {
        if (error.status >= 400) {
          console.warn("Could not update user:", formatApiRequestError(error));
        }
        setSaveButtonDisabled(false);
      });
  };

  const toggleModals = () => {
    if (
      !email.error &&
      !phoneNumber.error &&
      (user.email !== email.value || phoneNumberDb !== phoneNumber.value)
    ) {
      if (phoneNumberDb !== phoneNumber.value) {
        setCautionModalHeader("Confirm phone number changes");
        if (phoneNumber.value === "") {
          setCautionModalContent(
            "This will remove the phone number on your profile. You will receive an email to confirm the changes."
          );
        } else {
          setCautionModalContent(
            "This will update the phone number on your profile and will be used as new login credentials. You will receive an SMS on your new phone number to confirm changes. You are authorizing Swept to send an SMS."
          );
        }
      } else {
        setCautionModalHeader("Confirm email changes");
        setCautionModalContent(
          "This will update the email on your profile and will be used as new login credentials. You will receive an email on your new email address to confirm changes, this is essential to ensure your account is secured."
        );
      }
      toggleCautionModal();
    } else {
      handleSaveProfile();
    }
  };

  const toggleCautionModal = () => {
    setShowCautionModal(!showCautionModal);
  };

  const toggleServerErrorModal = () => {
    setShowServerErrorModal(!showServerErrorModal);
  };

  const validateConfirmPassword = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShouldBlockNavigation(true);

    const value = event.target.value.trim();
    const password = {
      ...confirmNewPassword,
      value,
      error: value !== newPassword.value,
      helperText:
        value !== newPassword.value
          ? "Must be the same as the new password."
          : "",
    };

    handleFieldValueChange("confirmNewPassword", password);
  };

  const validateEmail = (value: string) => {
    setShouldBlockNavigation(true);

    if (user.email.toLowerCase() !== value.toLowerCase()) {
      setIsEmailValidating(true);
      validateSignupEmailHelper(value.toLowerCase()).then((email) => {
        setIsEmailValidating(false);
        handleFieldValueChange("email", email as ValueType);
      });
    } else {
      setIsEmailValidating(false);
      handleFieldValueChange("email", {
        error: false,
        value: user.email,
        helperText: "",
      });
    }
  };

  const validateNewPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldBlockNavigation(true);
    const newPassword = event.target.value.trim();
    handleFieldValueChange("newPassword", validatePasswordHelper(newPassword));

    const confirmNewPasswordData = {
      ...confirmNewPassword,
      error: newPassword !== confirmNewPassword.value,
      helperText:
        newPassword !== confirmNewPassword.value
          ? "Must be the same as the new password."
          : "",
      value: confirmNewPassword.value,
    };
    handleFieldValueChange("confirmNewPassword", confirmNewPasswordData);
  };

  const validatePassword = (event: React.FocusEvent<HTMLInputElement>) => {
    setShouldBlockNavigation(true);
    const value = event.target.value;

    if (value) {
      setIsPasswordValidating(true);
      validateUserPasswordHelper(value, user.company.id).then(
        (currentPassword: any) => {
          setIsPasswordValidating(false);
          handleFieldValueChange("currentPassword", currentPassword);
        }
      );
    } else {
      setIsPasswordValidating(false);
      handleFieldValueChange("currentPassword", {
        error: false,
        value: value,
        helperText: "",
      });
    }
  };

  const validatePhoneNumber = (value: string, country: string | undefined) => {
    setShouldBlockNavigation(true);

    if (user.phoneNumber !== value) {
      setIsPhoneNumberValidating(true);
      validateSignupPhoneNumberHelper(value, country, true).then(
        (phoneNumber: any) => {
          setIsPhoneNumberValidating(false);
          setPhoneCountry({ value: country });
          handleFieldValueChange("phoneNumber", phoneNumber);
        }
      );
    } else {
      setIsPhoneNumberValidating(false);
      handleFieldValueChange("phoneNumber", {
        error: false,
        value: user.phoneNumber,
        helperText: "",
      });
    }
  };

  const validateText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldBlockNavigation(true);
    handleFieldValueChange(
      event.target.name,
      validateTextHelper(event.target.name, event.target.value)
    );
  };

  return (
    <>
      <StickyPageHeader title={"My Profile"}>
        <Button onClick={() => navigate(-1)} id="myprofile-cancel-btn">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          id="myprofile-save-btn"
          disabled={saveButtonDisabled}
          onClick={() => {
            checkFormErrors() && toggleModals();
          }}
        >
          Save
        </Button>
      </StickyPageHeader>

      {user && (
        <form>
          <Grid container spacing={2} className={classes.container}>
            <Grid item xs={12}>
              <Card>
                <CardContent className={classes.cardPadding}>
                  <Grid item xs={12} className={classes.avatarBox}>
                    <Avatar
                      name={userFullName}
                      size={"120"}
                      round
                      textSizeRatio={2.3}
                      maxInitials={2}
                      className={classes.avatar}
                    />
                  </Grid>

                  <BasicInfoComponent
                    changePhoneCountry={handleChangePhoneCountry}
                    classes={classes}
                    disableEmailField={disableEmailField}
                    disablePhoneField={disablePhoneField}
                    email={email}
                    firstName={firstName}
                    handleFieldChange={handleFieldChange}
                    lastName={lastName}
                    phoneCountry={phoneCountry}
                    phoneNumber={phoneNumber}
                    validateEmail={validateEmail}
                    validatePhoneNumber={validatePhoneNumber}
                    validateText={validateText}
                  />

                  <AddressInfoComponent
                    classes={classes}
                    country={country}
                    countryRef={countryRef}
                    handleFieldValueChange={handleFieldValueChange}
                    language={language}
                    languageList={languageList}
                    languageRef={languageRef}
                    setShouldBlockNavigation={setShouldBlockNavigation}
                    timezone={timezone}
                    timezoneList={timezoneList}
                    timezoneRef={timezoneRef}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {!showPasswordForm && (
            <Button
              variant="contained"
              color="inherit"
              className={classes.passwordLink}
              onClick={() => setShowPasswordForm(true)}
            >
              Change Password <LaunchOutlined />
            </Button>
          )}

          {showPasswordForm && (
            <Grid container spacing={2} className={classes.container}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title={
                      <>
                        <Typography variant="h5" className={classes.title}>
                          Password Settings
                        </Typography>
                      </>
                    }
                  />
                  <CardContent
                    className={classNames(
                      classes.cardBorder,
                      classes.cardPadding
                    )}
                  >
                    <Grid container item spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <PasswordFieldWithValidation
                          id="current-password"
                          inputProps={{ "data-testid": "current-password" }}
                          label="Current Password"
                          shrinkLabel={true}
                          name="currentPassword"
                          variant="outlined"
                          type="password"
                          autoComplete="current-password"
                          className={classes.textFieldFullWidth}
                          onBlur={validatePassword}
                          onChange={(event) =>
                            handleFieldChange(
                              "currentPassword",
                              event.target.value
                            )
                          }
                          error={currentPassword.error}
                          fullWidth
                          required
                          value={currentPassword.value}
                          helperText={currentPassword.helperText}
                        />
                      </Grid>

                      <Grid container item spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <PasswordFieldWithValidation
                            id="new-password"
                            inputProps={{ "data-testid": "new-password" }}
                            label="New Password"
                            shrinkLabel={true}
                            name="newPassword"
                            variant="outlined"
                            type="password"
                            autoComplete="new-password"
                            className={classes.textFieldFullWidth}
                            onChange={validateNewPassword}
                            error={newPassword.error}
                            helperText={
                              newPassword.error
                                ? "Invalid password: must be 6-85 characters long including an uppercase and lowercase letter, a number and a special character"
                                : "Must be 6-85 characters long including an uppercase and lowercase letter, a number and a special character."
                            }
                            fullWidth
                            required
                            value={newPassword.value}
                            disabled={
                              currentPassword.error ||
                              currentPassword.value === ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <PasswordFieldWithValidation
                            id="confirm-new-password"
                            inputProps={{
                              "data-testid": "confirm-new-password",
                            }}
                            label="Confirm New Password"
                            shrinkLabel={true}
                            name="confirmNewPassword"
                            variant="outlined"
                            type="password"
                            autoComplete="confirm-new-password"
                            className={classes.textFieldFullWidth}
                            onChange={validateConfirmPassword}
                            error={confirmNewPassword.error}
                            helperText={confirmNewPassword.helperText}
                            fullWidth
                            required
                            value={confirmNewPassword.value}
                            disabled={
                              currentPassword.error || newPassword.value === ""
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </form>
      )}

      <ModalFormConfirmation
        acceptButtonText="Confirm"
        header={cautionModalHeader}
        open={showCautionModal}
        onAccept={() => handleSaveProfile()}
        onDismiss={() => toggleCautionModal()}
        details={cautionModalContent}
        showCloseIcon={false}
      />

      <ModalServerTimeOut
        open={showServerErrorModal}
        onDismiss={toggleServerErrorModal}
      />

      <PromptUnsavedChanges when={shouldBlockNavigation} />
    </>
  );
};

export default ProfilePage;
